import { useCallback, useEffect, useState } from "react";
import { Play, Check, CircleDot, Circle, Download, Loader2, Trash2, RefreshCw, Wifi, WifiOff, CloudUpload, CircleCheck } from "lucide-react";
import { BookMark, ListenShell, fmtTime } from "./listenShared";
import { InstallHint } from "./InstallHint";
import * as offline from "@/lib/offline";

const FRONT_MATTER = new Set(["title_page", "dedication"]);
const fmtMB = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(0)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const estBytes = (secs) => Math.round((secs || 0) * 16000); // ~128 kbps

function StatusIcon({ status }) {
  if (status === "completed") return <Check className="h-4 w-4 text-teal-300" />;
  if (status === "in_progress") return <CircleDot className="h-4 w-4 text-amber-300" />;
  return <Circle className="h-4 w-4 text-slate-600" />;
}

function ConnBadge({ online, syncState }) {
  const map = {
    offline: { icon: WifiOff, text: "Offline", cls: "text-slate-400" },
    syncing: { icon: CloudUpload, text: "Syncing", cls: "text-amber-300" },
    pending: { icon: CloudUpload, text: "Waiting to sync", cls: "text-amber-300" },
    synced: { icon: online ? Wifi : WifiOff, text: online ? "Synced" : "Offline", cls: online ? "text-teal-300/80" : "text-slate-400" },
  };
  const s = !online ? map.offline : map[syncState] || map.synced;
  const Icon = s.icon;
  return <span data-testid="listen-conn-status" className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest ${s.cls}`}><Icon className="h-3 w-3" /> {s.text}</span>;
}

function DownloadControl({ item, state, busy, preview, online, onDownload, onRemove }) {
  if (preview || !item.has_audio) return null;
  if (busy) return <span data-testid={`listen-item-state-${item.item_id}`} className="text-teal-300"><Loader2 className="h-4 w-4 animate-spin" /></span>;
  if (state === "downloaded") return (
    <button data-testid={`listen-remove-${item.item_id}`} title="Remove download" onClick={(e) => { e.stopPropagation(); onRemove(item); }} className="text-teal-300/80 hover:text-red-300">
      <CircleCheck className="h-4 w-4" data-testid={`listen-item-state-${item.item_id}`} />
    </button>
  );
  if (state === "outdated") return (
    <button data-testid={`listen-download-${item.item_id}`} title="Update available" onClick={(e) => { e.stopPropagation(); onDownload(item); }} className="text-amber-300" disabled={!online}>
      <RefreshCw className="h-4 w-4" data-testid={`listen-item-state-${item.item_id}`} />
    </button>
  );
  return (
    <button data-testid={`listen-download-${item.item_id}`} title={online ? "Download" : "Connect to download"} onClick={(e) => { e.stopPropagation(); onDownload(item); }} className="text-slate-400 hover:text-teal-300 disabled:opacity-30" disabled={!online}>
      <Download className="h-4 w-4" data-testid={`listen-item-state-${item.item_id}`} />
    </button>
  );
}

export function ListenLibrary({ data, preview, online, syncState, token, cover, onPlay }) {
  const rel = data.release;
  const items = rel?.items || [];
  const relId = rel?.release_id;
  const cont = data.continue;
  const contItem = cont ? items.find((i) => i.item_id === cont.item_id) : null;
  const started = contItem && (contItem.position > 0 || contItem.status === "completed");

  const [states, setStates] = useState({});
  const [totalBytes, setTotalBytes] = useState(0);
  const [busy, setBusy] = useState({});
  const [allBusy, setAllBusy] = useState(false);
  const [err, setErr] = useState("");

  const refresh = useCallback(async () => {
    if (!relId || preview) return;
    const { states: s, totalBytes: tb } = await offline.downloadStates(relId, items);
    setStates(s); setTotalBytes(tb);
  }, [relId, items, preview]);

  useEffect(() => { refresh(); }, [refresh]);

  const dl = async (item) => {
    setErr(""); setBusy((b) => ({ ...b, [item.item_id]: true }));
    try { await offline.downloadItem(relId, item, token); }
    catch (e) { setErr(e.message === "auth" ? "Couldn't verify access — reconnect and try again." : "Download failed — tap to retry."); }
    finally { setBusy((b) => ({ ...b, [item.item_id]: false })); await refresh(); }
  };
  const rm = async (item) => { await offline.removeDownload(relId, item); await refresh(); };
  const rmAll = async () => { await offline.removeAllDownloads(relId); await refresh(); };

  const pending = items.filter((i) => i.has_audio && states[i.item_id] !== "downloaded");
  const totalSecs = items.reduce((n, i) => n + (i.has_audio ? (i.duration || 0) : 0), 0);
  const needBytes = pending.reduce((n, i) => n + estBytes(i.duration), 0);
  const allDownloaded = pending.length === 0 && items.some((i) => i.has_audio);

  const downloadAll = async () => {
    setErr(""); setAllBusy(true);
    try {
      const est = await offline.storageEstimate();
      if (est && est.available && needBytes > est.available) {
        setErr(`Not enough space (need ~${fmtMB(needBytes)}, ${fmtMB(est.available)} free). Remove some downloads first.`);
        return;
      }
      for (const it of pending) {
        setBusy((b) => ({ ...b, [it.item_id]: true }));
        try { await offline.downloadItem(relId, it, token); }
        catch { setErr("Some sections couldn't download — reconnect and retry."); }
        finally { setBusy((b) => ({ ...b, [it.item_id]: false })); }
        await refresh();
      }
    } finally { setAllBusy(false); await refresh(); }
  };

  return (
    <ListenShell footer={<InstallHint />}>
      <div className="mx-auto w-full max-w-lg px-5 pb-6 pt-8">
        <div className="flex items-center justify-between">
          <BookMark />
          <div className="flex items-center gap-3">
            <ConnBadge online={online} syncState={syncState} />
            {preview && <span data-testid="listen-preview-badge" className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest text-amber-300">Preview Mode</span>}
          </div>
        </div>

        <div className="mt-6 flex items-start gap-4">
          {cover && (
            <img
              data-testid="listen-library-cover"
              src={cover}
              alt={(data.book_title || "Book") + " cover art"}
              className="h-20 w-20 shrink-0 rounded-xl object-cover shadow-lg shadow-black/40 ring-1 ring-white/10"
            />
          )}
          <div className="min-w-0">
            <h1 className="font-serif text-3xl tracking-tight text-slate-50">{data.book_title || "House Diamond"}</h1>
            {data.author && <p className="mt-1 font-serif italic text-slate-400">{data.author}</p>}
            {rel && <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-600">{rel.name}</p>}
          </div>
        </div>

        {!rel || items.length === 0 ? (
          <div data-testid="listen-empty" className="mt-16 rounded-2xl border border-white/5 bg-white/[0.02] p-8 text-center">
            <p className="font-serif text-lg text-slate-300">Nothing to listen to yet</p>
          </div>
        ) : (
          <>
            {contItem && started && (
              <button data-testid="listen-continue-card" onClick={() => onPlay(cont.item_id, cont.position || 0)}
                className="mt-7 flex w-full items-center gap-4 rounded-3xl border border-teal-500/25 bg-gradient-to-br from-teal-500/[0.10] to-transparent p-5 text-left transition-all hover:border-teal-400/40 active:scale-[0.99]">
                <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-teal-400 text-slate-950 shadow-lg shadow-teal-500/20"><Play className="h-6 w-6" fill="currentColor" /></span>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-teal-300/80">Continue Listening</div>
                  <div className="mt-1 truncate font-serif text-xl text-slate-50">{cont.title}</div>
                  <div className="mt-0.5 font-mono text-xs text-slate-400">{fmtTime(cont.position)}</div>
                </div>
              </button>
            )}

            {!preview && (
              <div data-testid="listen-offline-panel" className="mt-6 rounded-2xl border border-white/5 bg-white/[0.02] p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Offline Listening</div>
                    <div className="mt-1 text-sm text-slate-300" data-testid="listen-storage-info">
                      {items.filter((i) => i.has_audio).length} sections · {fmtTime(totalSecs)} · {allDownloaded ? `${fmtMB(totalBytes)} downloaded` : `~${fmtMB(needBytes)}`}
                    </div>
                  </div>
                  {allDownloaded ? (
                    <button data-testid="listen-remove-all" onClick={rmAll} className="flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-2 text-xs text-slate-300 hover:border-red-400/40 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /> Remove all</button>
                  ) : (
                    <button data-testid="listen-download-all" onClick={downloadAll} disabled={allBusy || !online} className="flex items-center gap-1.5 rounded-full bg-teal-400 px-4 py-2 text-xs font-semibold text-slate-950 hover:bg-teal-300 disabled:opacity-40">
                      {allBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download all
                    </button>
                  )}
                </div>
                {err && <div data-testid="listen-download-error" className="mt-2 text-xs text-red-300">{err}</div>}
              </div>
            )}

            <div className="mt-8 mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-600">Contents</div>
            <div className="space-y-2">
              {items.map((it) => {
                const subtle = FRONT_MATTER.has(it.kind);
                return (
                  <div key={it.item_id} className={`flex items-center gap-2 rounded-2xl border px-2 ${subtle ? "border-white/5 bg-white/[0.015]" : "border-white/5 bg-white/[0.03]"}`}>
                    <button data-testid={`listen-item-${it.item_id}`} onClick={() => onPlay(it.item_id, it.position || 0)}
                      className="group flex flex-1 items-center gap-3 py-3.5 pl-2 text-left">
                      <StatusIcon status={it.status} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-3">
                          <span className={`truncate font-serif ${subtle ? "text-sm text-slate-300" : "text-[17px] text-slate-100"}`}>{it.title}</span>
                          <span className="shrink-0 font-mono text-[11px] text-slate-500">{fmtTime(it.duration)}</span>
                        </div>
                        {it.status === "in_progress" && it.duration > 0 && (
                          <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/5"><div className="h-full rounded-full bg-teal-400/70" style={{ width: `${it.percent}%` }} /></div>
                        )}
                      </div>
                    </button>
                    <div className="flex items-center gap-2 pr-2">
                      <DownloadControl item={it} state={states[it.item_id]} busy={busy[it.item_id]} preview={preview} online={online} onDownload={dl} onRemove={rm} />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </ListenShell>
  );
}
